import React, { useEffect, useState } from "react";
import { Box, Tooltip } from "@mui/material";

const ConnectionGauge = ({ size = 60 }) => {
  const [value, setValue] = useState(0);
  const [latency, setLatency] = useState(null);

  useEffect(() => {
    const checkConnection = async () => {
      const start = performance.now();

      try {
        await fetch(
          `https://www.cloudflare.com/cdn-cgi/trace?t=${Date.now()}`,
          {
            mode: "no-cors",
            cache: "no-store",
          },
        );

        const ms = Math.round(performance.now() - start);
        setLatency(ms);

        const strength = Math.max(0, Math.min(100, 100 - (ms / 500) * 100));
        setValue(strength);
      } catch {
        setLatency(null);
        setValue(0);
      }
    };

    checkConnection();
    const interval = setInterval(checkConnection, 5000);

    return () => clearInterval(interval);
  }, []);

  const getStatus = () => {
    if (value >= 85) {
      return {
        label: "Excellent",
        color: "#22c55e",
      };
    }

    if (value >= 60 && value < 85) {
      return {
        label: "Good",
        color: "#facc15",
      };
    }

    if (value >= 40 && value < 60) {
      return {
        label: "Slow",
        color: "#facc15",
      };
    }

    return {
      label: "Poor",
      color: "#ef4444",
    };
  };

  const status = getStatus();

  const center = 50;
  const radius = 37;

  const startAngle = -135;
  const endAngle = 135;

  const polar = (angle, r = radius) => {
    const radians = ((angle - 90) * Math.PI) / 180;

    return {
      x: center + r * Math.cos(radians),
      y: center + r * Math.sin(radians),
    };
  };

  const getPath = (start, end, r = radius) => {
    const startPoint = polar(end, r);
    const endPoint = polar(start, r);

    return `
      M ${startPoint.x} ${startPoint.y}
      A ${r} ${r} 0 0 0 ${endPoint.x} ${endPoint.y}
    `;
  };

  const segments = 10;
  const segmentGap = 3;

  const needleAngle = startAngle + ((endAngle - startAngle) * value) / 100;
  const needle = polar(needleAngle, 29);

  const getSegmentColor = (index) => {
    // Weakest → strongest
    if (index < 3) {
      return "#ef4444";
    }

    if (index < 7) {
      return "#facc15";
    }

    return "#22c55e";
  };

  return (
    <Tooltip
      title={
        latency !== null
          ? `${status.label} connection • ${latency} ms`
          : "No connection"
      }
      arrow
    >
      <Box
        sx={{
          width: size,
          position: "relative",
          textAlign: "center",
          cursor: "default",
        }}
      >
        <svg
          width={size}
          height={size}
          viewBox="0 0 100 100"
          style={{
            display: "block",
          }}
        >
          {/* Gauge */}
          {Array.from({ length: segments }).map((_, index) => {
            const segmentSize = (endAngle - startAngle) / segments;
            const start = startAngle + index * segmentSize + segmentGap / 2;
            const end = startAngle + (index + 1) * segmentSize - segmentGap / 2;

            return (
              <path
                key={index}
                d={getPath(start, end)}
                fill="none"
                stroke={getSegmentColor(index)}
                strokeWidth="10"
                strokeLinecap="butt"
              />
            );
          })}

          {/* Needle */}
          <line
            x1={center}
            y1={center}
            x2={needle.x}
            y2={needle.y}
            stroke="#111111"
            strokeWidth="3"
            strokeLinecap="round"
            style={{
              transition: "all 400ms ease",
            }}
          />

          {/* Needle center */}
          <circle
            cx={center}
            cy={center}
            r="6"
            fill="#ffffff"
            stroke="#111111"
            strokeWidth="3"
          />
        </svg>

        {/* Label */}
        <Box
          sx={{
            fontSize: "9px",
            fontWeight: 500,
            color: "text.secondary",
            whiteSpace: "nowrap",
            mt: -1,
          }}
        >
          Internet
        </Box>
      </Box>
    </Tooltip>
  );
};

export default ConnectionGauge;
